import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { useEffect, useRef } from "react";
import DeleteIcon from "../../../../../../../../../assets/images/icons/DeleteIcon";
import { EditIcon } from "../../../../../../../../../assets/images/icons/EditIcon";
import SolidGradientColorPicker from "../../../../../../../../../components/common/SolidGradientColorPicker/SolidGradientColorPicker";
import Close from "../../../../../../../../assets/svgs/Close.svg";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { Divider } from "rsuite";
import PlusIcon from "../../../../../../../../../assets/images/icons/PlusIcon";
import BriaIconButton from "../../../../../../../../../components/common/BriaIconButton/BriaIconButton";
import { useAppStore } from "../../../../../../../../../hooks/useStores";
import { ColorTypeMappingEnum } from "../../../../../../../../../models/common";
import common from "../../Common.module.scss";
import styles from "./ColorItem.module.scss";

interface ColorItemProps {
	color: string;
	colors: string[];
	index: number;
	isBrandTemplateEditor: boolean | undefined;
	setColors: (colors: string[]) => void;
	showError: boolean;
	activeColorIndex: number | null;
	setActiveColorIndex: (index: number | null) => void;
	isPublicInAnotherOrg?: boolean;
}

const ColorItem: React.FC<ColorItemProps> = ({
	color,
	colors,
	index,
	isBrandTemplateEditor,
	setColors,
	showError,
	activeColorIndex,
	setActiveColorIndex,
	isPublicInAnotherOrg,
}) => {
	const colorItemRef = useRef<HTMLDivElement>(null);
	const emptyColor = color.trim().length === 0;
	const colorKey = `COLOR_${index + 1}` as keyof typeof ColorTypeMappingEnum;
	const { t } = useTranslation("translation", { keyPrefix: "editor.tabs.brands" });
	const validColors = colors.filter((color, i) => color && i !== activeColorIndex);
	const { brandsDefinitionStore } = useAppStore();

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (colorItemRef.current && !colorItemRef.current.contains(event.target as Node)) {
				setActiveColorIndex(null);
			}
		};

		if (activeColorIndex === index) {
			document.addEventListener("mousedown", handleClickOutside);
		} else {
			document.removeEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [activeColorIndex, index]);

	const handleDeleteColor = (index: number) => {
		const updatedColors = [...colors];
		updatedColors[index] = "";
		setColors(updatedColors);
		const updatedColorPallet = {
			...brandsDefinitionStore.selectedBrand?.color_pallete,
			colors: updatedColors,
		};
		brandsDefinitionStore.handleBrandChange("color_pallete", updatedColorPallet);
	};

	const handleColorChange = (color: string, index: number) => {
		const updatedColors = [...colors];
		updatedColors[index] = color;
		setColors(updatedColors);
		const updatedColorPallet = {
			...brandsDefinitionStore.selectedBrand?.color_pallete,
			colors: updatedColors,
		};
		brandsDefinitionStore.handleBrandChange("color_pallete", updatedColorPallet);
	};

	const renderItemActions = () => {
		if (isBrandTemplateEditor && !isPublicInAnotherOrg) {
			return (
				<>
					<Box className={common.itemActions}>
						{color && (
							<BriaIconButton className={common.deleteBtn} onClick={() => handleDeleteColor(index)}>
								<DeleteIcon />
							</BriaIconButton>
						)}

						<BriaIconButton
							className={common.editBtn}
							onClick={() => {
								if (activeColorIndex !== index) {
									setActiveColorIndex(index);
								} else {
									setActiveColorIndex(null);
								}
							}}
						>
							<EditIcon />
						</BriaIconButton>
					</Box>
					{activeColorIndex === index && (
						<Box className={styles.colorPicker}>
							<Box>
								<Box className={styles.listHeader}>
									<Typography className={styles.selectedColorType}>
										{ColorTypeMappingEnum[colorKey]}
									</Typography>
									<Box className={styles.iconsContainer}>
										<BriaIconButton
											className={styles.actionBtn}
											onClick={() => {
												setActiveColorIndex(null);
											}}
										>
											<img src={Close} />
										</BriaIconButton>
									</Box>
								</Box>
								<Divider className={styles.divider} />
								<SolidGradientColorPicker
									hideColorTypeBtns
									color={color}
									onChange={(color) => {
										handleColorChange(color, index);
									}}
								/>
								<Divider className={styles.divider} />
								{validColors.length > 0 && (
									<Typography className={styles.brandColors}>{t("brandColors")}</Typography>
								)}
								<Box className={styles.selectedBrandColors}>
									{colors.map((color, visibleIndex) => {
										const currentColorKey = `COLOR_${
											visibleIndex + 1
										}` as keyof typeof ColorTypeMappingEnum;

										return color && visibleIndex !== activeColorIndex ? (
											<Box
												onClick={() => {
													handleColorChange(color, index);
												}}
												key={currentColorKey}
												className={styles.brandItemContent}
											>
												<Box sx={{ background: color }} className={common.itemPreview} />
												<Typography className={common.itemLabel}>
													{ColorTypeMappingEnum[currentColorKey]}
												</Typography>
											</Box>
										) : null;
									})}
								</Box>
							</Box>
						</Box>
					)}
				</>
			);
		}
	};

	return (
		<Box
			ref={colorItemRef}
			key={index}
			className={clsx(
				styles.ColorItemContainer,
				common.listItem,
				{ [common.editable]: isBrandTemplateEditor && !isPublicInAnotherOrg },
				{ [common.errorBackground]: showError },
			)}
		>
			<Box className={common.itemContent}>
				<Box
					sx={{ background: color }}
					className={clsx(common.itemPreview, {
						[common.emptyStatus]: emptyColor,
					})}
				>
					{!color && <PlusIcon />}
				</Box>
				<Typography className={common.itemLabel}>{ColorTypeMappingEnum[colorKey]}</Typography>
			</Box>
			<Box className={common.missingItem}>
				{showError && (
					<Box className={common.missingItemContainer}>
						<ErrorOutlineIcon className={common.errorIcon} />
						<Typography className={common.errorMessage}>{t("missingColor")}</Typography>
					</Box>
				)}
			</Box>
			{renderItemActions()}
		</Box>
	);
};

const ObservedComponent = observer(ColorItem);
export default ObservedComponent;
